import { IPermission, IPermissionApplication, IPermissionCollection } from 'src/types';

export interface IPermissionAction {
  slug: string;
  action: string;
  label: string;
  name: string;
  description: string | null;
  disabled?: boolean;
}

export interface IPermissionChildGroup {
  collection: string;
  label: string;
  description: string;
  actions: IPermissionAction[];
}

export interface IPermissionGroup {
  collection: string;
  label: string;
  description: string;
  readSlug: string | null;
  actions: IPermissionAction[];
  children: IPermissionChildGroup[];
}

export interface IApplicationSection {
  key: string;
  label: string;
  groups: IPermissionGroup[];
}

const DESTRUCTIVE_ACTIONS = new Set(['cancel', 'delete']);

export const isDestructiveAction = (action: string): boolean => DESTRUCTIVE_ACTIONS.has(action);

const ACTION_ORDER = [
  'read',
  'create',
  'run',
  'install',
  'update',
  'export',
  'review',
  'uninstall',
  'delete',
];

const ACTION_LABELS: Record<string, string> = {
  read: 'View',
  create: 'Create',
  run: 'Run',
  update: 'Edit',
  delete: 'Delete',
  export: 'Export',
  review: 'Review',
  install: 'Install',
  uninstall: 'Uninstall',
};

const capitalise = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const byActionOrder = (a: IPermissionAction, b: IPermissionAction): number => {
  const aIndex = ACTION_ORDER.indexOf(a.action);
  const bIndex = ACTION_ORDER.indexOf(b.action);

  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
  if (aIndex !== -1) return -1;
  if (bIndex !== -1) return 1;
  return a.action.localeCompare(b.action);
};

const toAction = (permission: IPermission): IPermissionAction => ({
  slug: permission.slug,
  action: permission.action,
  label: ACTION_LABELS[permission.action] ?? capitalise(permission.action),
  name: permission.name,
  description: permission.description,
});

const impliedViewAction = (collection: string): IPermissionAction => ({
  slug: `${collection}:read`,
  action: 'read',
  label: ACTION_LABELS.read,
  name: ACTION_LABELS.read,
  description: null,
  disabled: true,
});

export const buildPermissionSections = (
  applications: IPermissionApplication[],
  collections: IPermissionCollection[],
  permissions: IPermission[],
): IApplicationSection[] => {
  const actionsByCollection = permissions.reduce<Map<string, IPermissionAction[]>>(
    (grouped, permission) => {
      const actions = grouped.get(permission.collection) ?? [];
      actions.push(toAction(permission));
      grouped.set(permission.collection, actions);
      return grouped;
    },
    new Map(),
  );

  const sortedActions = (collection: string): IPermissionAction[] =>
    [...(actionsByCollection.get(collection) ?? [])].sort(byActionOrder);

  const buildGroup = (parent: IPermissionCollection): IPermissionGroup => {
    const ownActions = sortedActions(parent.key);
    const readSlug = ownActions.find((action) => action.action === 'read')?.slug ?? null;

    const children = collections
      .filter((collection) => collection.parent === parent.key)
      .map((collection) => ({
        collection: collection.key,
        label: collection.label,
        description: collection.description,
        actions: sortedActions(collection.key),
      }))
      .filter((child) => child.actions.length > 0)
      .sort((a, b) => a.label.localeCompare(b.label));

    // View is the implied baseline for any card. Without a real read
    // permission, a disabled always-on placeholder communicates it. It is
    // display-only — never submitted.
    const actions = readSlug ? ownActions : [impliedViewAction(parent.key), ...ownActions];

    return {
      collection: parent.key,
      label: parent.label,
      description: parent.description,
      readSlug,
      actions,
      children,
    };
  };

  return applications
    .map((application) => ({
      key: application.key,
      label: application.label,
      groups: collections
        .filter((collection) => collection.application === application.key && !collection.parent)
        .map(buildGroup)
        .filter(
          (group) => group.actions.some((action) => !action.disabled) || group.children.length > 0,
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .filter((section) => section.groups.length > 0);
};

const selectable = (actions: IPermissionAction[]): IPermissionAction[] =>
  actions.filter((action) => !action.disabled);

export const allGroupActions = (group: IPermissionGroup): IPermissionAction[] => [
  ...selectable(group.actions),
  ...group.children.flatMap((child) => selectable(child.actions)),
];

export const togglePermission = (
  selected: string[],
  group: IPermissionGroup,
  slug: string,
  checked: boolean,
): string[] => {
  if (checked) {
    const additions = group.readSlug && slug !== group.readSlug ? [slug, group.readSlug] : [slug];
    return Array.from(new Set([...selected, ...additions]));
  }

  if (slug === group.readSlug) {
    const cardSlugs = new Set(allGroupActions(group).map((action) => action.slug));
    return selected.filter((permission) => !cardSlugs.has(permission));
  }

  return selected.filter((permission) => permission !== slug);
};

export const toggleGroup = (
  selected: string[],
  group: IPermissionGroup,
  checked: boolean,
): string[] => {
  const cardSlugs = allGroupActions(group).map((action) => action.slug);

  if (checked) {
    return Array.from(new Set([...selected, ...cardSlugs]));
  }

  const cardSet = new Set(cardSlugs);
  return selected.filter((permission) => !cardSet.has(permission));
};

export const isGroupFullySelected = (selected: string[], group: IPermissionGroup): boolean => {
  const actions = allGroupActions(group);
  return actions.length > 0 && actions.every((action) => selected.includes(action.slug));
};

export const isGroupPartiallySelected = (selected: string[], group: IPermissionGroup): boolean =>
  allGroupActions(group).some((action) => selected.includes(action.slug)) &&
  !isGroupFullySelected(selected, group);
